/**
 * Listbox — accessible single-select list with full keyboard navigation.
 *
 * Single responsibility: render a vertical list of items as the ARIA
 * `listbox` / `option` pair, manage the roving-tabindex pattern, and
 * dispatch arrow / Home / End / mouse-click selection events. Item content
 * is supplied by the caller via `renderItem` so the component is generic
 * over any item shape.
 *
 * Selection model: this listbox follows "selection-follows-navigation".
 * Arrow keys, Home, and End immediately update the selection (no separate
 * commit step). The visible "active" row is therefore always the same as
 * the externally driven `selectedId` — they never diverge.
 *
 * Accessibility:
 *   - The roving-tabindex pattern is used: `role="option"` and
 *     `aria-selected` live on the focusable element (the option button),
 *     and only the active option carries `tabIndex={0}` — every other
 *     option is `tabIndex={-1}`. The wrapper `<ul role="listbox">` is
 *     never directly focusable; `aria-activedescendant` is not used,
 *     because focus is held by the option element itself.
 *   - Keyboard handling is attached to each option; the active option
 *     therefore receives ArrowUp/Down/Home/End directly via its DOM
 *     event handlers (delegated to `useKeyboardListNav`).
 *   - Mouse clicks fire `onSelect` and shift focus to the clicked option
 *     so subsequent arrow keys begin from the just-clicked row.
 *   - When `selectedId` changes via external state (filters, search),
 *     the new active row is scrolled into view inside the listbox.
 *
 * Robustness:
 *   - If `selectedId` is `null` or refers to an id not in `items`
 *     (e.g. a stale selection after filtering), the active id is clamped
 *     to the first item so `aria-activedescendant`-style references and
 *     the roving tabindex always point at a real option.
 *
 * Design:
 *   - Generic `<T>` lets callers constrain the item shape; `getId(item)`
 *     extracts the stable identifier used for refs and ARIA wiring.
 *   - The component is otherwise presentation-agnostic — chrome (border,
 *     scroll container) is rendered by Card/wrappers around it; only the
 *     interactive list is owned here.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React, { useEffect, useMemo, useRef } from "react";
import { GRAY, PRIMARY } from "../design-system/tokens.js";
import { useKeyboardListNav } from "../hooks/useKeyboardListNav.js";

/**
 * Render context passed to the consumer.
 *
 * `isSelected` reflects the externally driven selection from `selectedId`.
 * `isActive` reflects the row that currently holds the roving tab stop —
 * under this listbox's selection-follows-navigation model the two flags
 * are equal, but `isActive` is exposed separately so callers can style
 * the focus ring even if the parent has not yet propagated the new
 * `selectedId` back into props on the same render.
 */
export interface ListboxRenderState {
  readonly isSelected: boolean;
  readonly isActive: boolean;
}

export interface ListboxProps<T> {
  /** Items to render. */
  readonly items: readonly T[];
  /** Currently selected id, or null when nothing is selected. */
  readonly selectedId: string | null;
  /** Selection handler; called for both mouse clicks and keyboard activation. */
  readonly onSelect: (id: string) => void;
  /** Stable identifier extractor — must return distinct ids per item. */
  readonly getId: (item: T) => string;
  /** Item renderer; receives the item and per-item state flags. */
  readonly renderItem: (item: T, state: ListboxRenderState) => React.ReactNode;
  /** Accessible name for the listbox. */
  readonly ariaLabel: string;
  /**
   * Optional id-prefix for option DOM ids (defaults to "listbox-option").
   * Set this when multiple Listboxes appear on the same page so option ids
   * stay unique and assistive technology can reference them by id.
   */
  readonly optionIdPrefix?: string;
  /** Optional max-height for the scroll container (default: 32rem). */
  readonly maxHeight?: string;
  /** Optional empty-state node rendered when `items` is empty. */
  readonly empty?: React.ReactNode;
}

const LIST_STYLE: React.CSSProperties = {
  listStyle: "none",
  margin: 0,
  padding: 0,
  overflowY: "auto",
};

const OPTION_BUTTON_STYLE_BASE: React.CSSProperties = {
  display: "block",
  width: "100%",
  textAlign: "left",
  padding: "0.625rem 1rem",
  border: "none",
  cursor: "pointer",
  color: GRAY[900],
};

/**
 * Generic accessible single-select listbox.
 *
 * Memoized via `React.memo`; callers should keep `getId` and `renderItem`
 * referentially stable (e.g. via `useCallback`) when items rerender often.
 *
 * Implementation note: the body uses a typed cast over `React.memo` so the
 * exported component preserves its generic signature, mirroring the pattern
 * used by `FilterBar`.
 */
function ListboxImpl<T>({
  items,
  selectedId,
  onSelect,
  getId,
  renderItem,
  ariaLabel,
  optionIdPrefix = "listbox-option",
  maxHeight = "32rem",
  empty,
}: ListboxProps<T>): React.JSX.Element | null {
  const optionRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  const itemIds = useMemo(() => items.map((item) => getId(item)), [items, getId]);

  // Clamp the active cursor to a real option. If `selectedId` is null or
  // refers to an id not in the current items list (e.g. a stale selection
  // after filtering), fall back to the first item so the roving tabindex
  // always lands on something focusable.
  const activeId = useMemo(() => {
    if (selectedId !== null && itemIds.includes(selectedId)) return selectedId;
    return itemIds[0];
  }, [selectedId, itemIds]);

  const { onKeyDown } = useKeyboardListNav<string>({
    items: itemIds,
    activeId: activeId ?? "",
    onSelect: (id): void => {
      onSelect(id);
      optionRefs.current.get(id)?.focus();
    },
  });

  useEffect(() => {
    if (selectedId === null) return;
    const node = optionRefs.current.get(selectedId);
    // Guard the call so the component works under test runtimes that do not
    // implement `scrollIntoView` (e.g. jsdom). At runtime in real browsers
    // the method is always present.
    if (node !== undefined && typeof node.scrollIntoView === "function") {
      node.scrollIntoView({ block: "nearest" });
    }
  }, [selectedId]);

  if (items.length === 0) {
    return empty !== undefined ? <>{empty}</> : null;
  }

  return (
    <ul role="listbox" aria-label={ariaLabel} style={{ ...LIST_STYLE, maxHeight }}>
      {items.map((item, index) => {
        const id = getId(item);
        const isSelected = id === selectedId;
        const isActive = id === activeId;
        return (
          // The <li> is a plain layout wrapper. Per the roving-tabindex
          // pattern, role="option", aria-selected, the ARIA id, and the
          // tabIndex live on the focusable element (the inner button) so
          // the focused element matches the element advertised as the
          // active option.
          <li
            key={id}
            style={{
              borderBottom: index === items.length - 1 ? "none" : `1px solid ${GRAY[100]}`,
            }}
          >
            <button
              type="button"
              role="option"
              id={`${optionIdPrefix}-${id}`}
              aria-selected={isSelected}
              ref={(node): void => {
                if (node) optionRefs.current.set(id, node);
                else optionRefs.current.delete(id);
              }}
              tabIndex={isActive ? 0 : -1}
              onClick={(): void => {
                onSelect(id);
              }}
              onKeyDown={onKeyDown}
              style={{
                ...OPTION_BUTTON_STYLE_BASE,
                background: isSelected ? PRIMARY[100] : "white",
              }}
            >
              {renderItem(item, { isSelected, isActive })}
            </button>
          </li>
        );
      })}
    </ul>
  );
}

/**
 * `React.memo` strips generics; the typed cast restores the generic signature
 * while keeping the memoized runtime body. Same pattern as `FilterBar`.
 *
 * Migration: new call-sites should prefer the Radix-backed `Select`
 * primitive (`./primitives/Select.tsx`), which ships WAI-ARIA
 * semantics, keyboard navigation, and focus management by construction.
 * `Listbox` remains exported because the inline (always-visible,
 * roving-tabindex) selection flavor it supplies is not covered by
 * Radix Select. Tracked by issue #1577.
 */
export const Listbox = React.memo(ListboxImpl) as <T>(
  props: ListboxProps<T>,
) => React.JSX.Element | null;
